import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageSEO from '../components/PageSEO';
import {
  Activity, Heart, Scale, Syringe, User, BookOpen,
  TrendingUp, TrendingDown, ArrowRight, Shield, BarChart3,
  Users, Brain, Microscope, MapPin, ChevronDown, Clock, Linkedin,
  Radar, Search, ClipboardList, PieChart, Target, AlertCircle
} from 'lucide-react';
import { useCountUp } from '../hooks/useCountUp';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { BRAND, WHAT_THIS_SITE_DOES, EPIDEMIOLOGY_CORE, REAL_WORLD_APPLICATIONS } from '../lib/brand';
import { SITE_MEDIA } from '../lib/siteMedia';
import { STATE_FIPS, GEO_URL, getRiskColor } from '../lib/mapConstants';
import { fetchWithFallback } from '../lib/dataFetcher';
const API = process.env.REACT_APP_BACKEND_URL;
const STATES_LIST = ["US","AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const IMAGES = {
  hero: SITE_MEDIA.heroPoster,
  immune: SITE_MEDIA.autoimmune,
  vaccine: SITE_MEDIA.vaccine,
  wellness: SITE_MEDIA.education,
  data: SITE_MEDIA.chronic,
};

const anim = (d = 0) => ({ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay: d, duration: 0.5 } });

const CountStat = ({ icon: Icon, label, value, suffix, color, trend, delay }) => {
  const isNum = !isNaN(parseFloat(value));
  const { count, ref } = useCountUp(isNum ? parseFloat(value) : 0, 1800);
  return (
    <motion.div {...anim(delay)} ref={ref} className="rounded-[1.35rem] border border-white/15 bg-white/10 p-4 shadow-[0_20px_60px_rgba(32,14,53,0.24)] backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:bg-white/15" data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-4 h-4 text-white" /></div>
        {trend === 'up' && <TrendingUp className="w-3.5 h-3.5 text-red-400" />}
        {trend === 'down' && <TrendingDown className="w-3.5 h-3.5 text-emerald-400" />}
        {trend === 'stable' && <Activity className="w-3.5 h-3.5 text-amber-400" />}
      </div>
      <p className="text-2xl font-bold text-white tracking-tight">{isNum ? count : value}<span className="text-sm text-white/50 ml-0.5">{suffix}</span></p>
      <p className="text-[11px] text-white/60 mt-0.5">{label}</p>
    </motion.div>
  );
};

export default function HomePage() {
  const [stats, setStats] = useState(null);
  const [selectedState, setSelectedState] = useState('US');
  const [category, setCategory] = useState('chronic');
  const [mapData, setMapData] = useState([]);
  const [mapDisease, setMapDisease] = useState('diabetes');
  const [hoveredState, setHoveredState] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedState !== 'US') params.set('state', selectedState);
    if (category) params.set('category', category);
    fetchWithFallback(`/api/home-stats?${params}`).then(d => d && setStats(d)).catch(() => {});
  }, [selectedState, category]);

  useEffect(() => {
    fetchWithFallback(`/api/chronic/map-data?disease=${mapDisease}`)
      .then(d => d && setMapData(d.data || []))
      .catch(() => {});
  }, [mapDisease]);

  const mapLookup = useMemo(() => { const m = {}; mapData.forEach(d => { m[d.state] = d; }); return m; }, [mapData]);

  const categories = [
    { key: 'chronic', label: 'Chronic Disease', icon: Heart },
    { key: 'autoimmune', label: 'Autoimmune', icon: Shield },
    { key: 'vaccine', label: 'Vaccines', icon: Syringe },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <PageSEO title="Home" description="EpiSplain makes epidemiology accessible. Explore chronic disease maps, autoimmune disease data, vaccine coverage, interactive risk calculators, and evidence-based public health education sourced from CDC, NIH, and WHO." path="/" />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(181,126,220,0.24),_transparent_22%),radial-gradient(circle_at_80%_20%,_rgba(106,13,173,0.16),_transparent_24%),linear-gradient(135deg,_#160826_0%,_#4B1D8F_38%,_#6A0DAD_68%,_#B57EDC_100%)]">
          <video
            className="h-full w-full object-cover opacity-45"
            autoPlay
            muted
            loop
            playsInline
            poster={SITE_MEDIA.heroPoster}
          >
            <source src={SITE_MEDIA.heroVideo} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(12,6,21,0.72),rgba(75,29,143,0.58),rgba(181,126,220,0.42))]" />
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '48px 48px' }} />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#f8f5ff]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <motion.div {...anim(0)} className="text-center max-w-4xl mx-auto mb-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-violet-100 shadow-sm backdrop-blur-sm mb-5" data-testid="hero-badge">
              <Shield className="w-4 h-4" /> Real public health data · clearer explanations · real-world context
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-5">
              {BRAND.headline}
            </h1>
            <p className="text-sm sm:text-base lg:text-lg text-violet-50/90 leading-relaxed max-w-3xl mx-auto">
              {BRAND.subheadline}
            </p>
          </motion.div>

          <motion.div {...anim(0.15)} className="flex flex-wrap items-center justify-center gap-3 mb-8" data-testid="hero-controls">
            <div className="flex gap-1 rounded-2xl border border-white/15 bg-white/[0.08] p-1.5 shadow-lg shadow-violet-950/10 backdrop-blur-md">
              {categories.map(c => (
                <button key={c.key} onClick={() => setCategory(c.key)} data-testid={`hero-cat-${c.key}`}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all ${category === c.key ? 'bg-white text-violet-900 shadow-md' : 'text-white/70 hover:text-white hover:bg-white/10'}`}>
                  <c.icon className="w-3.5 h-3.5" /> {c.label}
                </button>
              ))}
            </div>
          </motion.div>

          {stats?.highlight_stat && (
            <motion.div key={category} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="text-center mb-6">
              <p className="text-lg sm:text-xl font-bold text-violet-100">{stats.highlight_stat}</p>
              <p className="text-sm text-violet-100/70">{stats.highlight_detail}</p>
            </motion.div>
          )}

          {stats?.vs_national && (
            <motion.div key={selectedState} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-wrap justify-center gap-3 mb-6" data-testid="state-comparison">
              {Object.entries(stats.vs_national).map(([k, v]) => (
                <span key={k} className={`text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm border ${v > 0 ? 'bg-red-500/20 border-red-300/40 text-red-100' : v < 0 ? 'bg-emerald-500/20 border-emerald-300/40 text-emerald-100' : 'bg-white/10 border-white/20 text-white/70'}`}>
                  {k.replace('_', ' ')}: {v > 0 ? '+' : ''}{v}% vs US avg
                </span>
              ))}
            </motion.div>
          )}

        </div>
      </section>

      {/* ========== WHAT THIS SITE DOES ========== */}
      <section className="py-12 bg-white border-b border-violet-100/80" data-testid="story-cards">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <p className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-700 mb-4">
              What This Site Does
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-3">Make public health data understandable, meaningful, and useful.</h2>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
              EpiSplain turns disease trends, vaccine patterns, and population health data into explanations that feel grounded in real life—not just dashboards.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {WHAT_THIS_SITE_DOES.map((card, i) => (
              <motion.div key={card.title} {...anim(0.05 + i * 0.08)} className="brand-card group p-6" data-testid={`story-card-${i}`}>
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-700 via-fuchsia-600 to-purple-400 text-2xl shadow-lg shadow-violet-900/15 group-hover:scale-105 transition-transform">
                  <span aria-hidden="true">{card.emoji}</span>
                </div>
                <h3 className="text-lg font-bold text-slate-950 mb-2">{card.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{card.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== UNDERSTANDING EPIDEMIOLOGY ========== */}
      <section className="py-12 bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 text-white" data-testid="epidemiology-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...anim(0)} className="text-center mb-10">
            <p className="inline-flex items-center gap-2 rounded-full border border-violet-400/30 bg-violet-500/20 px-4 py-1.5 text-sm font-semibold text-violet-200 mb-4">
              <Microscope className="w-4 h-4" /> Core Discipline
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">What is Epidemiology?</h2>
            <p className="text-base text-violet-100/80 max-w-3xl mx-auto leading-relaxed">
              {EPIDEMIOLOGY_CORE.what}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <motion.div {...anim(0.1)} className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Why It Matters</h3>
              </div>
              <p className="text-sm text-violet-100/80 leading-relaxed">{EPIDEMIOLOGY_CORE.why}</p>
            </motion.div>
            <motion.div {...anim(0.15)} className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <PieChart className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">How It Works</h3>
              </div>
              <p className="text-sm text-violet-100/80 leading-relaxed">{EPIDEMIOLOGY_CORE.how}</p>
            </motion.div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {REAL_WORLD_APPLICATIONS.map((app, i) => {
              const icons = { radar: Radar, search: Search, clipboard: ClipboardList, chart: BarChart3 };
              const Icon = icons[app.icon] || AlertCircle;
              return (
                <motion.div key={app.title} {...anim(0.1 + i * 0.05)} className="rounded-2xl bg-white/5 border border-white/10 p-5 hover:bg-white/10 transition-colors group">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h4 className="font-bold text-white mb-2">{app.title}</h4>
                  <p className="text-xs text-violet-200/70 leading-relaxed mb-2">{app.description}</p>
                  <p className="text-[11px] text-violet-300/60 italic">Example: {app.example}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div {...anim(0.3)} className="mt-10 text-center">
            <Link to="/learn" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold transition-colors">
              Start Learning Epidemiology <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ========== ABOUT MARIA / EPISPLAIN ========== */}
      <section id="about-episplain-section" className="py-12 bg-gradient-to-br from-white via-violet-50/40 to-white border-b border-violet-100" data-testid="about-episplain-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-6 items-stretch">
            <div className="brand-card p-7 sm:p-8 relative overflow-hidden">
              <p className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-700 mb-4">
                About EpiSplain
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-3">Created to make epidemiology understandable, human, and useful.</h2>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-3xl">
                EpiSplain is a mission-driven public health platform shaped around one idea: people should not need a graduate degree to understand the health data that affects their lives. The goal is to translate surveillance data, disease trends, and prevention evidence into language that feels clear, grounded, and actionable.
              </p>
              <div className="grid sm:grid-cols-3 gap-3 mt-6">
                {[
                  { title: 'Who', body: 'Maria Mongiardo — Epidemiologist focused on making public health evidence easier to understand.' },
                  { title: 'Why', body: 'To bridge the gap between technical datasets and real-world decisions people make every day.' },
                  { title: 'How', body: 'By combining transparent sources, plain-English interpretation, and interactive visuals.' },
                ].map((item) => (
                  <div key={item.title} className="rounded-2xl border border-violet-100 bg-white/90 p-4 shadow-sm">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-700 mb-2">{item.title}</p>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-[1.75rem] border border-violet-200/70 bg-slate-950 text-white p-7 sm:p-8 shadow-[0_24px_70px_rgba(76,29,143,0.14)] relative overflow-hidden">
              <div className="absolute inset-0 brand-gradient opacity-30" />
              <div className="relative">
                <p className="text-sm font-semibold text-violet-100 mb-2">Connect with Maria Mongiardo</p>
                <h3 className="text-2xl font-bold mb-3">If this mission resonates, let’s connect.</h3>
                <p className="text-sm text-slate-200 leading-relaxed mb-6">
                  Follow the work, share ideas for new datasets, and connect around epidemiology, health communication, and public-interest data storytelling.
                </p>
                <a
                  href="https://www.linkedin.com/in/mariamongiardo/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full brand-button px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-900/20"
                >
                  <Linkedin className="w-4 h-4" /> Connect with me on Maria Mongiardo LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ========== FEATURED INTERACTIVE MAP ========== */}
      <section className="py-12 bg-slate-50" data-testid="featured-map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between mb-6 gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-1">Explore the Map</h2>
              <p className="text-sm text-slate-500">Hover over any state to see prevalence, trend, and comparison vs national average</p>
            </div>
            <div className="flex gap-2" data-testid="map-disease-filter">
              {[
                { key: 'diabetes', label: 'Diabetes', color: '#f97316' },
                { key: 'obesity', label: 'Obesity', color: '#8b5cf6' },
                { key: 'heart_disease', label: 'Heart Disease', color: '#ef4444' },
                { key: 'hypertension', label: 'Hypertension', color: '#dc2626' },
              ].map(d => (
                <button key={d.key} onClick={() => setMapDisease(d.key)} data-testid={`map-filter-${d.key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${mapDisease === d.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Map */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200/60 p-4 shadow-sm" data-testid="featured-map">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3 text-[10px]">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> Low</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-500" /> Moderate</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500" /> High</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-slate-200" /> No Data</span>
                </div>
                <span className="text-[10px] text-slate-400">Source: CDC PLACES 2025</span>
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <ComposableMap 
                  projection="geoAlbersUsa" 
                  projectionConfig={{ scale: 900 }}
                  width={780} 
                  height={450}
                  style={{ width: '100%', height: 'auto' }}
                >
                  <Geographies geography={GEO_URL}>
                    {({ geographies }) => geographies.map(geo => {
                      const sa = STATE_FIPS[geo.id];
                      const sd = mapLookup[sa];
                      return (
                        <Geography key={geo.rsmKey || geo.id} geography={geo}
                          fill={hoveredState === sa ? '#1e293b' : sd ? getRiskColor(sd.risk_level) : '#e2e8f0'}
                          stroke="#fff" strokeWidth={0.75}
                          onMouseEnter={() => setHoveredState(sa)}
                          onMouseLeave={() => setHoveredState(null)}
                          style={{ default: { outline: 'none' }, hover: { outline: 'none', cursor: 'pointer' }, pressed: { outline: 'none' } }} />
                      );
                    })}
                  </Geographies>
                </ComposableMap>
              </div>
            </div>

            {/* Insights Panel */}
            <div className="space-y-4" data-testid="map-insights-panel">
              {hoveredState && mapLookup[hoveredState] ? (
                <motion.div key={hoveredState} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm">
                  <h3 className="text-2xl font-bold text-slate-900 mb-1">{hoveredState}</h3>
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">{mapDisease.replace('_', ' ')} rate</span>
                      <span className="text-lg font-bold" style={{ color: getRiskColor(mapLookup[hoveredState].risk_level) }}>{mapLookup[hoveredState].rate}%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(mapLookup[hoveredState].rate / 50) * 100}%`, backgroundColor: getRiskColor(mapLookup[hoveredState].risk_level) }} />
                    </div>
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>US Avg: {mapLookup[hoveredState].us_average}%</span>
                      <span className={mapLookup[hoveredState].difference > 0 ? 'text-red-600 font-semibold' : 'text-emerald-600 font-semibold'}>
                        {mapLookup[hoveredState].difference > 0 ? '+' : ''}{mapLookup[hoveredState].difference}%
                      </span>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-xs font-bold text-blue-700 mb-1">What This Means</p>
                    <p className="text-xs text-slate-700 leading-relaxed mb-2">
                      {mapLookup[hoveredState].risk_level === 'high'
                        ? `${hoveredState} has significantly higher ${mapDisease.replace('_', ' ')} rates than the national average. This may be driven by factors like healthcare access, socioeconomic conditions, and lifestyle patterns.`
                        : mapLookup[hoveredState].risk_level === 'low'
                        ? `${hoveredState} is performing better than average for ${mapDisease.replace('_', ' ')}. Continued investment in prevention and healthcare access helps maintain these lower rates.`
                        : `${hoveredState} is near the national average for ${mapDisease.replace('_', ' ')}. Targeted prevention programs could help reduce rates further.`}
                    </p>
                    <div className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${mapLookup[hoveredState].data_status === 'estimated' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {mapLookup[hoveredState].data_status === 'estimated' ? 'Estimate used' : 'Official CDC data'}
                    </div>
                    {mapLookup[hoveredState].coverage_note && <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">{mapLookup[hoveredState].coverage_note}</p>}
                  </div>
                </motion.div>
              ) : hoveredState ? (
                <div className="bg-white rounded-2xl border border-amber-200 p-5 shadow-sm text-center">
                  <MapPin className="w-10 h-10 mx-auto text-amber-300 mb-3" />
                  <p className="text-sm font-semibold text-amber-800">No recent CDC data available</p>
                  <p className="text-xs text-amber-700">We don't currently have a recent value for {hoveredState} for this metric.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm text-center">
                  <MapPin className="w-10 h-10 mx-auto text-slate-200 mb-3" />
                  <p className="text-sm font-semibold text-slate-400">Hover over a state</p>
                  <p className="text-xs text-slate-400">to see detailed health data</p>
                </div>
              )}

              <Link to="/chronic-disease" className="block bg-slate-900 text-white rounded-2xl p-4 hover:bg-slate-800 transition-colors group">
                <div className="flex items-center gap-3">
                  <BarChart3 className="w-5 h-5 text-teal-400" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold">Full Chronic Disease Dashboard</p>
                    <p className="text-xs text-slate-400">State maps, trends, demographics, and risk factors</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              <Link to="/risk" className="block bg-amber-50 border border-amber-200 rounded-2xl p-4 hover:bg-amber-100 transition-colors group">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-amber-600" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900">See Yourself in the Data</p>
                    <p className="text-xs text-slate-500">Personalized risk based on your profile</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-amber-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== AUTOIMMUNE SPOTLIGHT ========== */}
      <section className="py-12 bg-gradient-to-br from-purple-50 to-violet-50 border-y border-purple-100" data-testid="autoimmune-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...anim(0.1)} className="flex flex-col lg:flex-row items-center gap-8">
            <div className="lg:w-1/3">
              <img src={IMAGES.immune} alt="Immune cells" className="w-full h-56 object-cover rounded-2xl shadow-lg" />
            </div>
            <div className="lg:w-2/3">
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Autoimmune Diseases: Often Missed, Often Delayed</h2>
              <p className="text-sm text-slate-600 mb-5 leading-relaxed">Over 80 autoimmune diseases affect an estimated 24 million Americans. Symptoms can be vague, diagnosis often takes years, and women are disproportionately affected. EpiSplain keeps this topic separate so it can be understood on its own terms.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Americans Affected', value: '24M', icon: Users, color: 'bg-purple-100 text-purple-700' },
                  { label: 'Known Diseases', value: '80+', icon: Brain, color: 'bg-violet-100 text-violet-700' },
                  { label: 'Patients Female', value: '78%', icon: Heart, color: 'bg-pink-100 text-pink-700' },
                  { label: 'Avg Diagnosis Delay', value: '4.5yr', icon: Clock, color: 'bg-indigo-100 text-indigo-700' },
                ].map((s, i) => (
                  <div key={i} className={`rounded-xl p-3 ${s.color.split(' ')[0]} border border-purple-200/50`}>
                    <s.icon className={`w-5 h-5 mb-1 ${s.color.split(' ')[1]}`} />
                    <p className="text-xl font-bold text-slate-900">{s.value}</p>
                    <p className="text-[10px] text-slate-500">{s.label}</p>
                  </div>
                ))}
              </div>
              <Link to="/autoimmune" className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-purple-600 hover:text-purple-700">
                Explore autoimmune data <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Impact + Sources */}
      <section className="py-10 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { value: '$4.1T', label: 'Annual chronic disease cost', color: 'border-red-200 bg-red-50' },
              { value: '7 in 10', label: 'US deaths from chronic disease', color: 'border-amber-200 bg-amber-50' },
              { value: '80%', label: 'Chronic disease is preventable', color: 'border-emerald-200 bg-emerald-50' },
              { value: '33%', label: 'Cancer death rate drop since 1991', color: 'border-blue-200 bg-blue-50' },
            ].map((n, i) => (
              <div key={i} className={`rounded-2xl border ${n.color} p-5 text-center`}>
                <p className="text-2xl font-bold text-slate-900">{n.value}</p>
                <p className="text-xs text-slate-600 mt-1">{n.label}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-5 text-slate-400">
            {['CDC PLACES 2025', 'CDC BRFSS', 'NIH/NIAID', 'WHO', 'MA DPH', 'IHME'].map(s => (
              <span key={s} className="flex items-center gap-1.5 text-[10px] font-medium"><Shield className="w-3 h-3 text-teal-500" /> {s}</span>
            ))}
            <Link to="/sources" className="text-[10px] font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-1">
              View all sources <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
